import { formatSharePercent } from "@normax-performance/shared-format";
import { formatAnalyticsHoursMinutes, formatAnalyticsMinutes } from "../../lib/format";
import "./Donut.css";

export interface DonutSegment {
  key: string;
  value: number;
  label: string;
  minutes: string;
  colorVar: string;
}

export interface DonutProps {
  segments: DonutSegment[];
  size?: number;
  thickness?: number;
  centerPrimary?: string;
  centerSecondary?: string;
  ariaLabel: string;
  intlLocale: string;
  hasData?: boolean;
  /** Legend values: "raw" (default) keeps the existing `formatAnalyticsMinutes`
   * behavior (every current caller except Dashboard/Analytics' own
   * top-level summary donut, which opts into "hours" — see
   * `formatAnalyticsHoursMinutes`'s own doc comment for why only those two
   * are aggregate totals large enough to read better as hours). */
  minutesDisplay?: "raw" | "hours";
}

/**
 * Hand-built SVG donut for the "where did the recorded time go" summary
 * (Normirani rad / Neutralne aktivnosti / Rad bez norme). No charting
 * dependency. Segment colours are CSS custom properties so it themes with
 * tokens.css. Values need not sum to 1 — the component normalizes.
 * Spec: docs/superpowers/specs/2026-09-10-visual-uplift-design.md §4.
 */
export function Donut({
  segments,
  size = 168,
  thickness = 22,
  centerPrimary,
  centerSecondary,
  ariaLabel,
  intlLocale,
  hasData = true,
  minutesDisplay = "raw",
}: DonutProps) {
  const r = (size - thickness) / 2;
  const cx = size / 2;
  const cy = size / 2;
  const circumference = 2 * Math.PI * r;

  const total = segments.reduce((sum, s) => sum + Math.max(0, s.value), 0) || 1;
  const drawn = hasData ? segments.filter((s) => s.value > 0) : [];

  let offsetAccum = 0;
  const arcs = drawn.map((s) => {
    const frac = Math.max(0, s.value) / total;
    const dash = `${frac * circumference} ${circumference}`;
    const dashoffset = -offsetAccum * circumference;
    offsetAccum += frac;
    return { key: s.key, colorVar: s.colorVar, dash, dashoffset };
  });

  const legendText = drawn.length
    ? segments
        .map((s) => `${s.label} ${formatSharePercent(s.value / total, 1)}`)
        .join(", ")
    : "—";

  return (
    <div className="nx-donut">
      <svg
        className="nx-donut__svg"
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        role="img"
        aria-label={`${ariaLabel}: ${legendText}`}
      >
        {!hasData || arcs.length === 0 ? (
          <circle
            className="nx-donut__track-empty"
            cx={cx}
            cy={cy}
            r={r}
            fill="none"
            strokeWidth={thickness}
          />
        ) : (
          arcs.map((a) => (
            <circle
              key={a.key}
              className="nx-donut__arc"
              cx={cx}
              cy={cy}
              r={r}
              fill="none"
              strokeWidth={thickness}
              strokeDasharray={a.dash}
              strokeDashoffset={a.dashoffset}
              style={{ stroke: `var(${a.colorVar})` }}
              transform={`rotate(-90 ${cx} ${cy})`}
            />
          ))
        )}
        {(centerPrimary || centerSecondary) && (
          <>
            {centerPrimary && (
              <text className="nx-donut__center-primary" x={cx} y={cy} textAnchor="middle" dominantBaseline="middle">
                {centerPrimary}
              </text>
            )}
            {centerSecondary && (
              <text
                className="nx-donut__center-secondary"
                x={cx}
                y={cy + thickness}
                textAnchor="middle"
                dominantBaseline="middle"
              >
                {centerSecondary}
              </text>
            )}
          </>
        )}
      </svg>
      <ul className="nx-donut__legend">
        {segments.map((s) => (
          <li key={s.key} className="nx-donut__legend-item">
            <span
              className="nx-donut__swatch"
              style={{ background: `var(${s.colorVar})` }}
              aria-hidden="true"
            />
            <span className="nx-donut__legend-label">{s.label}</span>
            <span className="nx-donut__legend-value">
              {hasData
                ? minutesDisplay === "hours"
                  ? formatAnalyticsHoursMinutes(s.minutes, intlLocale)
                  : `${formatAnalyticsMinutes(s.minutes, intlLocale)} min`
                : "—"}
              {hasData && (
                <span className="nx-donut__legend-share"> · {formatSharePercent(s.value / total, 1)}</span>
              )}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
